import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { initAnalytics } from "../../firebase/config";
import { useDisclosure } from "../../hooks/useDisclosure";
import { useLocalStorage } from "../../hooks/useLocalStorage";

const STORAGE_KEY = "cookie_consent";

interface CookiePreferences {
  essential: boolean;
  analytical: boolean;
  functional: boolean;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  essential: true,
  analytical: true,
  functional: true,
};

function applyPreferences(prefs: CookiePreferences) {
  if (prefs.analytical) {
    initAnalytics();
  }
}

function Toggle({
  enabled,
  disabled,
  onChange,
}: {
  enabled: boolean;
  disabled?: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors duration-200 ${
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
      } ${enabled ? "bg-[#00A3FF]" : "bg-white/20"}`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 mt-0.5 ${
          enabled ? "translate-x-5 ml-0.5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function PreferencesModal({
  isOpen,
  onClose,
  onSave,
  initialPrefs,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (prefs: CookiePreferences) => void;
  initialPrefs: CookiePreferences | null;
}) {
  const [prefs, setPrefs] = useState<CookiePreferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    if (isOpen) {
      setPrefs(initialPrefs ?? DEFAULT_PREFERENCES);
    }
  }, [isOpen, initialPrefs]);

  if (!isOpen) return null;

  const categories = [
    {
      key: "essential" as const,
      label: "Essential",
      description:
        "Required for the site to function. These cannot be disabled.",
      locked: true,
    },
    {
      key: "analytical" as const,
      label: "Analytical",
      description:
        "Help us understand how visitors use the site so we can improve it.",
      locked: false,
    },
    {
      key: "functional" as const,
      label: "Functional",
      description:
        "Enable enhanced features and personalisation, such as remembering your preferences.",
      locked: false,
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="fixed inset-0 bg-black/60" />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-md bg-card-bg border border-white/10 rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-2">
          <h2 className="text-lg font-semibold text-white">
            Cookie Preferences
          </h2>
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 pb-2">
          <p className="text-white/60 text-sm">
            Choose which cookies you'd like to allow. See our{" "}
            <Link
              to="/legal/cookie-policy"
              className="text-brand-blue hover:underline"
              onClick={onClose}
            >
              Cookie Policy
            </Link>{" "}
            for details.
          </p>
        </div>

        <div className="px-6 py-4 space-y-4">
          {categories.map((cat) => (
            <div
              key={cat.key}
              className="flex items-start justify-between gap-4"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white">{cat.label}</p>
                <p className="text-xs text-white/50 mt-0.5">
                  {cat.description}
                </p>
              </div>
              <Toggle
                enabled={prefs[cat.key]}
                disabled={cat.locked}
                onChange={(val) => setPrefs({ ...prefs, [cat.key]: val })}
              />
            </div>
          ))}
        </div>

        <div className="px-6 pb-5 pt-2 flex items-center justify-between">
          <button
            onClick={() =>
              onSave({ essential: true, analytical: false, functional: false })
            }
            className="text-xs text-white/40 hover:text-white/70 transition-colors duration-200"
          >
            Decline all
          </button>
          <button
            onClick={() => onSave(prefs)}
            className="px-5 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#00A3FF] to-[#0077CC] hover:shadow-lg hover:shadow-[#00A3FF]/30 rounded-lg transition-all duration-200"
          >
            Save Preferences
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function CookieConsent() {
  const banner = useDisclosure();
  const prefsModal = useDisclosure();
  const [storedPrefs, setStoredPrefs] =
    useLocalStorage<CookiePreferences | null>(STORAGE_KEY, null);

  useEffect(() => {
    if (storedPrefs) {
      applyPreferences(storedPrefs);
    } else {
      banner.open();
    }
  }, []);

  const handleAcceptAll = () => {
    const prefs: CookiePreferences = {
      essential: true,
      analytical: true,
      functional: true,
    };
    setStoredPrefs(prefs);
    banner.close();
    applyPreferences(prefs);
  };

  const handleSavePreferences = (prefs: CookiePreferences) => {
    setStoredPrefs(prefs);
    prefsModal.close();
    banner.close();
    applyPreferences(prefs);
  };

  return (
    <>
      <AnimatePresence>
        {banner.isOpen && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4"
          >
            <div className="max-w-4xl mx-auto bg-card-bg border border-white/10 rounded-xl px-6 py-4 flex flex-col sm:flex-row items-center gap-4 shadow-2xl">
              <p className="text-white/80 text-sm flex-1 text-center sm:text-left">
                We use cookies to improve your experience. By continuing to use
                this site, you agree to our{" "}
                <Link
                  to="/legal/cookie-policy"
                  className="text-brand-blue hover:underline"
                >
                  Cookie Policy
                </Link>
                .
              </p>
              <div className="flex gap-3 shrink-0">
                <button
                  onClick={prefsModal.open}
                  className="px-4 py-2 text-sm font-medium text-white/70 hover:text-white border border-white/20 hover:border-white/40 rounded-lg transition-colors duration-200"
                >
                  Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#00A3FF] to-[#0077CC] hover:shadow-lg hover:shadow-[#00A3FF]/30 rounded-lg transition-all duration-200"
                >
                  Accept
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {prefsModal.isOpen && (
          <PreferencesModal
            isOpen={prefsModal.isOpen}
            onClose={prefsModal.close}
            onSave={handleSavePreferences}
            initialPrefs={storedPrefs}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default CookieConsent;
